/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { LogoWrapper, breakpoints } from '@coursera/cds-core';

import { NAV_SQUARE_LOGO_SIZE } from 'bundles/unified-description-page-common/constants';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { PartnerFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';

import _t from 'i18n!nls/xdp';

const HERO_PARTNER_LOGO_HEIGHT = 36;
const ALTERNATE_HERO_PARTNER_LOGO_HEIGHT = 18;

const originalStyles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  navDivider: css`
    background: var(--cds-color-grey-50);
    height: ${NAV_SQUARE_LOGO_SIZE}px;
    width: 2px;
    margin: 0 var(--cds-spacing-200);
  `,
  heroDivider: css`
    background: var(--cds-color-grey-300);
    height: var(--cds-spacing-400);
    width: 2px;
    margin: 0 var(--cds-spacing-300);
    ${breakpoints.down('xs')} {
      height: var(--cds-spacing-300);
      margin: 0 var(--cds-spacing-100);
    }
  `,
  image: css`
    max-width: 100%;
    height: ${HERO_PARTNER_LOGO_HEIGHT}px;
    ${breakpoints.down('sm')} {
      height: 30px;
    }
    ${breakpoints.down('xs')} {
      height: ${ALTERNATE_HERO_PARTNER_LOGO_HEIGHT}px;
    }
  `,
};

const redesignStyles = {
  ...originalStyles,
  image: css`
    height: 30px;
  `,
};

type Props = {
  partners: PartnerFragment[];
  isNavPlacement: boolean;
};

export const CoBrandingPartnerLogos = ({ partners, isNavPlacement }: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignHero;

  const logoField = isNavPlacement ? 'squareLogo' : 'primaryLogo';
  const styles = enableXDPRedesign ? redesignStyles : originalStyles;

  return (
    <div css={styles.container}>
      {partners
        .filter((partner) => !!partner[logoField])
        .map((partner, index) => (
          <React.Fragment key={partner.name || index}>
            <LogoWrapper paddingVariant={isNavPlacement ? 'smallOutside' : 'mediumOutside'}>
              <Imgix
                src={partner[logoField]!}
                alt={partner.name || _t('Partner')}
                maxHeight={isNavPlacement ? NAV_SQUARE_LOGO_SIZE : HERO_PARTNER_LOGO_HEIGHT}
                {...(isNavPlacement && { maxWidth: NAV_SQUARE_LOGO_SIZE })}
                css={[!isNavPlacement && styles.image]}
              />
            </LogoWrapper>
            {index !== partners.length - 1 && (
              <div
                data-testid="partner-co-branding-divider"
                css={isNavPlacement ? styles.navDivider : styles.heroDivider}
              />
            )}
          </React.Fragment>
        ))}
    </div>
  );
};

export default CoBrandingPartnerLogos;
